<script lang="ts">
  import Icon from './Icon.svelte';
  /**
   * Multi-value tag input — пілюлі (bubbles) з ✕ + текстове поле знизу.
   * Створення тегу: Enter, кома, пробіл, або blur.
   * Видалення: клік на ✕ біля пілюлі або Backspace у пустому input.
   *
   * Value передається ззовні як CSV-string (для сумісності з рештою системи,
   * де custom_fields зберігаються у jsonb як plain strings). Назовні
   * віддається теж CSV (онсейв) — щоб працювало з типовим Person.customFields.
   */
  let {
    value,
    onSave,
    placeholder = 'Додати тег…',
    readOnly = false,
  }: {
    value: string | null;
    onSave: (next: string | null) => Promise<void> | void;
    placeholder?: string;
    readOnly?: boolean;
  } = $props();

  /** Парсимо вхід-string на масив унікальних не-порожніх тегів. */
  function parse(v: string | null | undefined): string[] {
    if (!v) return [];
    return Array.from(
      new Set(
        v
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
      ),
    );
  }

  let tags = $state<string[]>(parse(value));
  let input = $state('');
  let busy = $state(false);

  // Sync якщо value змінився ззовні (наприклад після refresh).
  $effect(() => {
    tags = parse(value);
  });

  async function commit(next: string[]) {
    const csv = next.length === 0 ? null : next.join(', ');
    if (csv === (value ?? null)) return;
    busy = true;
    try {
      await onSave(csv);
    } finally {
      busy = false;
    }
  }

  function addFromInput() {
    const v = input.trim().replace(/,$/, '').trim();
    input = '';
    if (!v) return;
    if (tags.includes(v)) return;
    const next = [...tags, v];
    tags = next;
    void commit(next);
  }

  function removeTag(idx: number) {
    const next = tags.filter((_, i) => i !== idx);
    tags = next;
    void commit(next);
  }

  function onKey(e: KeyboardEvent) {
    if (readOnly) return;
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addFromInput();
    } else if (e.key === 'Backspace' && input === '' && tags.length > 0) {
      e.preventDefault();
      removeTag(tags.length - 1);
    }
  }

  function onBlur() {
    if (!readOnly) addFromInput();
  }

  /** Палітра кольорів для пілюль — за хешем рядка для стабільності. */
  function tagColor(t: string): string {
    const colors = [
      'bg-purple-100 text-purple-800 border-purple-200',
      'bg-blue-100 text-blue-800 border-blue-200',
      'bg-emerald-100 text-emerald-800 border-emerald-200',
      'bg-amber-100 text-amber-800 border-amber-200',
      'bg-rose-100 text-rose-800 border-rose-200',
      'bg-cyan-100 text-cyan-800 border-cyan-200',
      'bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200',
      'bg-lime-100 text-lime-800 border-lime-200',
    ];
    let hash = 0;
    for (let i = 0; i < t.length; i++) {
      hash = (hash * 31 + t.charCodeAt(i)) | 0;
    }
    return colors[Math.abs(hash) % colors.length]!;
  }
</script>

<div class="flex flex-wrap items-center gap-1.5 rounded-md border border-transparent hover:border-[var(--color-border-3)] focus-within:border-[var(--color-pu)] focus-within:bg-white px-1 py-0.5 min-h-[28px]">
  {#each tags as t, i (t)}
    <span
      class="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold {tagColor(t)}"
    >
      <span>{t}</span>
      {#if !readOnly}
        <button
          type="button"
          class="inline-flex items-center text-current opacity-60 hover:opacity-100"
          onclick={() => removeTag(i)}
          aria-label="Видалити тег {t}"
        ><Icon name="close" size={11} /></button>
      {/if}
    </span>
  {/each}
  {#if !readOnly}
    <input
      bind:value={input}
      onkeydown={onKey}
      onblur={onBlur}
      placeholder={tags.length === 0 ? placeholder : ''}
      class="flex-1 min-w-[80px] bg-transparent text-[12px] py-0.5 outline-none disabled:opacity-50"
      disabled={busy}
    />
  {/if}
</div>
